import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { WebSpeechApiService } from '../library/web-speech-api.service';
import { ControlService } from '../library/control.service';
import { AppConstants } from 'src/app/app-constants';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { DeviceSettingsSpeechRecognitionComponent } from '../device-settings/device-settings-speech-recognition/device-settings-speech-recognition.component';
import { Subscription } from 'rxjs';
import { PlayerService } from '../library/player.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { RealtimeService } from '../library/realtime/realtime.service';
import { CallFlagService } from 'src/app/services/learner/call-flag/call-flag.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { injectAiNotice } from 'src/app/pages/learner/call/library/ai-notice/ai-notice';

@Component({
  host: {
    class: 'theme-dark responsive',
  },
  standalone: false,
  selector: 'app-call-practice',
  templateUrl: './call-practice.component.html',
  styleUrls: [
    './call-practice.component.scss',
    './call-practice.component.css',
  ],
})
export class CallPracticeComponent implements OnInit, OnDestroy, AfterViewInit {
  // The account's AI notice in the sim's or learner's language; null hides it.
  protected readonly aiNotice = injectAiNotice();

  @Input() webSpeechApiService: WebSpeechApiService | undefined;
  @Input() controlService: ControlService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() realtimeService: RealtimeService | undefined;
  @Input() practiceCallUuid: string = '';

  @ViewChild('chromaCanvas') chromaCanvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild('avatarVideoRealtime') avatarVideoRef!: ElementRef<HTMLVideoElement>;

  enableVisualAid: boolean = false;

  visualAidClick: boolean = false;
  currentVisualAidPage = 0;

  advanceTypeManual: string = AppConstants.ADVANCE_TYPE_MANUAL;
  advanceTypeAuto: string = AppConstants.ADVANCE_TYPE_AUTOMATIC;

  errorSubscription!: Subscription;

  selectedPortal: Portal<any> | null = null;
  componentPortal: ComponentPortal<DeviceSettingsSpeechRecognitionComponent> | null =
    null;

  userName = { first: '', last: '', full: '' };
  timeoutResize: any;

  private chromaKeyStarted = false;

  // Set from the call's own value in ngOnInit().
  hideResultsForLearners = false;

  constructor(
    private amplitudeService: AmplitudeService,
    public callFlagService: CallFlagService,
    public callStore: CallStoreService,
    private logger: LoggerService
  ) {}

  get callMode(): 'normal' | 'visualAid' | 'realTime' {
    if (this.controlService?.isVisualAidCall && this.controlService?.isRealTimeCall) {
      return 'realTime';
    }
    if (this.controlService?.isVisualAidCall) {
      return 'visualAid';
    }
    return 'normal';
  }

  private getUserName() {
    const firstName = localStorage.getItem('user_first_name')?.trim() || 'User';
    const lastName = localStorage.getItem('user_last_name')?.trim() || '';

    return {
      first: firstName,
      last: lastName,
      full: `${firstName} ${lastName}`.trim(),
    };
  }

  trackBySrc(index: number, item: { src: string }) {
    return item.src;
  }

  private trackVisualAidPageChange() {
    this.playerService?.handleSendUserResponseVisualAid(
      `page-${this.visualAidClick ? this.currentVisualAidPage + 1 : 0} | `
    );
  }

  private handleNextClip(): void {
    if (this.controlService?.isVisualAidCall) {
      this.handleVisualAidNextClip();
    } else {
      this.playerService?.nextClip();
    }
  }

  private handleVisualAidNextClip(): void {
    this.trackVisualAidPageChange();
    this.callStore.setIsContinueButtonBlocked(true);
    if (this.playerService) {
      this.callStore.setIsSendingAnswer(true);
    }
    setTimeout(() => {
      this.playerService?.nextClipVisualAid();
    }, 2500);
  }

  private async handleVisualAidCallEnd(): Promise<void> {
    if (this.playerService) {
      this.trackVisualAidPageChange();
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    this.controlService?.endCallVisualAid();
  }

  protected get showPauseButton() {
    if (this.controlService?.isRealTimeCall) {
      return !!this.controlService.pauseEnabled;
    }
    return true;
  }

  protected get isPauseEnabled() {
    if (this.controlService?.isRealTimeCall) {
      return (
        !!this.controlService.pauseEnabled &&
        !!this.realtimeService?.hasJoinedSession
      );
    }
    return true;
  }

  protected get showHints() {
    return (
      this.callStore.getHints() != '' &&
      !this.controlService?.displayTourHint &&
      (!this.controlService?.isRealTimeCall ||
        (this.controlService?.isRealTimeCall &&
          this.controlService?.callType === AppConstants.CALL_TYPE_GUIDED))
    );
  }

  protected get showHintsTour() {
    return this.controlService?.displayTourHint;
  }

  protected get showTour() {
    const show = this.controlService?.displayTour;
    // On tavus calls we show the tour only if the call type is guided
    if (this.controlService?.isRealTimeCall) {
      return (
        show && this.controlService?.callType === AppConstants.CALL_TYPE_GUIDED
      );
    }

    // On synthesia calls, we show the tour if the loading screen is not shown
    if (this.playerService?.showLoadingScreen) return false;

    return show;
  }

  /**
   * Logic on whether to show or hide the continue button.
   */
  protected get showContinueButton() {
    const isRealTimeCall = this.controlService?.isRealTimeCall;
    // On real time call, show continue button only if the call type is guided
    if (isRealTimeCall) {
      const isGuidedCall =
        this.controlService?.callType === AppConstants.CALL_TYPE_GUIDED;
      if (isGuidedCall) {
        const hideContinue = this.callStore.getIsContinueButtonHidden();
        // On guided calls, a call can be completed but the continue button can still be visible
        return !hideContinue;
      }
      return false;
    }

    // Hide continue button if the call is complete
    if (this.callStore.getIsCallComplete()) return false;

    return this.callStore.getAdvanceType() === this.advanceTypeManual;
  }

  /**
   * Check if user has responded and can continue to next clip.
   */

  protected get canContinueNext() {
    if (!this.controlService || !this.playerService) {
      return false;
    }

    return (
      (!this.callStore.getIsPaused() &&
        !this.callStore.getIsContinueButtonBlocked() &&
        (this.callStore.getSpeechTranscript().length !== 0 ||
          this.callStore.getSpeechTranscriptPartial().length !== 0)) ||
      this.controlService?.displayTour
    );
  }

  protected get isContinueButtonDisabled() {
    // If it's a real-time call, we also check if the avatar is thinking to set the continue button disabled
    if (this.controlService?.isRealTimeCall) {
      return (
        !this.canContinueNext &&
        (this.callStore.getIsAvatarSpeaking() ||
          this.callStore.getIsAvatarThinking())
      );
    }
    return !this.canContinueNext && this.callStore.getIsAvatarSpeaking();
  }

  protected handleContinueClick() {
    if (this.controlService?.displayTour) {
      return;
    }

    if (this.canContinueNext) {
      const isRealTimeCall = this.controlService?.isRealTimeCall;
      if (isRealTimeCall) {
        this.realtimeService?.handleClickContinue();
      } else {
        this.handleNextClip();
      }
    } else {
      this.controlService?.clickOpenNoResponse();
    }
  }

  protected get showAvatarSpeakingBorder() {
    if (this.isDocumentPipOpen) return false;
    return (
      (!this.playerService?.isLoopActive &&
        !this.callStore.getIsPaused() &&
        this.playerService?.canPlayVideo &&
        !this.controlService?.isRealTimeCall) ||
      (this.controlService?.isRealTimeCall &&
        this.callStore.getIsAvatarSpeaking())
    );
  }

  protected get showAvatarSpeakingIndicator() {
    if (this.isDocumentPipOpen) return false;
    const isIdleAndReadyNonTavus =
      !this.playerService?.isLoopActive &&
      !this.callStore.getIsPaused() &&
      !this.callStore.getIsAvatarThinking() &&
      !this.controlService?.isRealTimeCall;

    const isTavusAndSpeaking =
      this.controlService?.isRealTimeCall &&
      this.callStore.getIsAvatarSpeaking();

    return isIdleAndReadyNonTavus || isTavusAndSpeaking;
  }

  protected get showAvatarLoadingIndicator() {
    if (this.isDocumentPipOpen) return false;
    return (
      this.callStore.getIsAvatarThinking() &&
      !this.callStore.getIsAvatarSpeaking()
    );
  }

  protected get showUserInitials() {
    return (
      this.callStore.getIsPaused() ||
      this.deviceSettingsService?.isCameraUnavailable ||
      !this.controlService?.isVideoOn
    );
  }

  protected get showUserSpeakingBorder() {
    if (!this.controlService || !this.playerService || this.isDocumentPipOpen) {
      return false;
    }

    return (
      (this.playerService.isLoopActive &&
        !this.callStore.getIsPaused() &&
        this.callStore.getIsMicOn() &&
        !this.playerService.isWaitingConversationalResponse &&
        !this.callStore.getIsSendingAnswer() &&
        !this.controlService.isRealTimeCall) ||
      (this.controlService.isRealTimeCall &&
        !this.callStore.getIsAvatarSpeaking())
    );
  }

  protected get showUserSpeakingIndicator() {
    if (!this.controlService || !this.playerService || this.isDocumentPipOpen) {
      return false;
    }

    if (this.controlService.isRealTimeCall) {
      if (this.realtimeService?.isFirstMessage) {
        return false;
      }
    }

    return (
      this.callStore.getIsMicOn() &&
      this.deviceSettingsService?.isUserSpeaking &&
      ((!this.callStore.getIsPaused() &&
        !this.playerService.isWaitingConversationalResponse &&
        this.playerService.isLoopActive &&
        !this.callStore.getIsSendingAnswer() &&
        !this.controlService.isRealTimeCall) ||
        this.controlService.isRealTimeCall)
    );
  }

  protected get disableMicButton() {
    // Disable mic button during tavus call first message
    if (this.controlService?.isRealTimeCall) {
      return !!this.realtimeService?.isFirstMessage;
    }
    return false;
  }

  protected get disableEndCallButton() {
    // Disable end call button during tavus call first message
    if (this.controlService?.isRealTimeCall) {
      return this.callStore.getDisableEndCallButton();
    }
    return false;
  }

  protected get showScreenshareButton(): boolean {
    return !!this.callStore.getCall()?.screenshareEnabled;
  }

  protected get isScreenSharing(): boolean {
    return !!this.realtimeService?.isScreenSharing();
  }

  protected get isScreenshareDisabled(): boolean {
    return (
      !this.realtimeService?.isAvatarVideoReady ||
      !!this.realtimeService?.isScreenSharePending() ||
      !!this.realtimeService?.isPipClosing()
    );
  }

  public handleScreenshareClick(): void {
    this.realtimeService?.toggleScreenShare();
  }

  protected get isAvatarInPip(): boolean {
    return !!this.realtimeService?.isAvatarInPip();
  }

  protected get isDocumentPipOpen(): boolean {
    return !!this.realtimeService?.isDocumentPipOpen();
  }

  protected get screenPreviewStream(): MediaStream | null {
    return this.isAvatarInPip ? (this.realtimeService?.screenPreviewStream() ?? null) : null;
  }

  protected get avatarDisplayName(): string {
    return this.screenPreviewStream ? '' : this.playerService?.avatarName || '';
  }

  protected get canReopenPip(): boolean {
    return !!this.realtimeService?.canReopenPip();
  }

  protected handlePipReopenClick(): void {
    this.sendAmplitudeClickPipReopenEvent();
    this.realtimeService?.reopenPip();
  }

  ngOnInit() {
    this.userName = this.getUserName();

    this.hideResultsForLearners = !!this.callStore.getCall()?.hideResultsForLearners;

    this.callStore.setDisplayPostCall(false);
    // Subscribing to error message, waiting for the error that can emit this in the webSpeechApiService logic
    if (this.webSpeechApiService) {
      this.errorSubscription = this.webSpeechApiService
        .getError()
        .subscribe(() => {
          if (this.componentPortal === null) {
            this.componentPortal = new ComponentPortal(
              DeviceSettingsSpeechRecognitionComponent
            );
          }
          this.selectedPortal = this.componentPortal;
        });
    }

    window.addEventListener('online', () => {
      this.logger.log('[CALL] You are back online!');
    });

    window.addEventListener('offline', () => {
      this.logger.log('[CALL] You are offline!');
    });

    if (this.controlService) {
      const display = this.controlService.shouldDisplayTour();
      console.log(`DEBUG: CallPracticeComponent ngOnInit - displayTour: ${display}`);
      if (display) {
        this.logger.log('[CALL] tour displayed');
        this.controlService.displayTour = true;
      }
    }
  }

  ngOnDestroy(): void {
    console.log('CallPracticeComponent ngOnDestroy triggered.');
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }

    // remove speech recognition validation interval
    if (this.controlService) {
      if (this.controlService.intervalSpeechDetected) {
        clearInterval(this.controlService.intervalSpeechDetected);
      }
    }
    if (this.realtimeService) {
      this.realtimeService.stopChromaKeying();
    }
  }

  ngAfterViewInit(): void {
    const isRealTimeCall = this.controlService?.isRealTimeCall;

    if (this.controlService && !isRealTimeCall) {
      this.controlService.pauseAfterInactivity();
    }

    if (this.deviceSettingsService) {
      this.deviceSettingsService.startAudioLevelDetection();
    }

    if (this.playerService && isRealTimeCall) {
      this.callStore.setIsAvatarSpeaking(false);
      return;
    }

    setTimeout(() => {
      if (!isRealTimeCall) {
        this.userVideoService?.displayUserVideo();
      }
    }, 100);
  }

  ngAfterViewChecked(): void {
    if (
      this.controlService?.isRealTimeCall &&
      this.avatarVideoRef &&
      this.chromaCanvasRef &&
      !this.chromaKeyStarted &&
      this.realtimeService?.customBackgroundUrl
    ) {
      const video = this.avatarVideoRef.nativeElement;
      const canvas = this.chromaCanvasRef.nativeElement;
      if (video && canvas) {
        this.chromaKeyStarted = true;
        console.log('Starting chroma keying with custom background URL:', this.realtimeService.customBackgroundUrl);
        this.realtimeService?.startChromaKeying(video, canvas, this.realtimeService.customBackgroundUrl);
      }
    }
  }

  copyToClipboard(): void {
    const uuid = this.callStore.getCall()?.practiceCallUuid || '';
    navigator.clipboard.writeText(uuid);
  }

  startCallAfterTour(): void {
    if (this.controlService) {
      this.controlService.displayTourHint = false;
      this.controlService.displayTour = false;
      const isRealTime = this.controlService.isRealTimeCall;
      const isGuided = this.realtimeService?.isGuided();
      if (isRealTime && isGuided) {
        this.realtimeService?.startCallRequest(false);
      } else {
        this.controlService.unpauseCall();
      }
    }
  }

  onPageChanged(page: number) {
    this.currentVisualAidPage = page;
  }

  onDisplayVisualAidClick(visualAidClickState: boolean) {
    this.visualAidClick = visualAidClickState;

    if (!this.visualAidClick && this.userVideoService) {
      this.userVideoService.displayUserVideo();
    }
  }

  public sendAmplitudeClickPauseEvent(): void {
    // Sending the Event of Clicking on Pause to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_PAUSE, {
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickMuteEvent(): void {
    // Sending the Event of Clicking on Mute to Amplitude
    this.amplitudeService.trackEvent(AmplitudeTrackingEvents.CALL_CLICK_MUTE, {
      mute_status: this.callStore.getIsMicOn()
        ? AmplitudeTrackingEventPropValues.MUTE_STATUS_UNMUTED
        : AmplitudeTrackingEventPropValues.MUTE_STATUS_MUTED,
      call_type: this.controlService?.callType,
    });
  }

  public sendAmplitudeClickCameraEvent(): void {
    // Sending the Event of Clicking on Start/Stop Video to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_CAMERA,
      {
        camera_status: this.controlService?.isVideoOn
          ? AmplitudeTrackingEventPropValues.CAMERA_STATUS_CAMERA_ON
          : AmplitudeTrackingEventPropValues.CAMERA_STATUS_CAMERA_OFF,
        call_type: this.controlService?.callType,
      }
    );
  }

  public sendAmplitudeClickEndCallEvent(): void {
    // Sending the Event of Clicking on End Call to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_ENDCALL,
      {
        call_type: this.controlService?.callType,
      }
    );
  }

  protected sendAmplitudeClickPipReopenEvent(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.CALL_CLICK_SCREENSHARE_PIP_REOPEN,
      {
        call_type: this.controlService?.callType,
      }
    );
  }

  // Click Handlers

  public handlePauseClick(): void {
    this.logger.log('[CALL] pause click');

    this.sendAmplitudeClickPauseEvent();
    this.controlService?.togglePause();
  }

  public handleMuteClick(): void {
    this.sendAmplitudeClickMuteEvent();
    this.controlService?.toggleMic();
  }

  public handleCameraClick(): void {
    this.sendAmplitudeClickCameraEvent();
    this.controlService?.toggleVideo();
  }

  public async handleEndCallClick(): Promise<void> {
    this.logger.log('[CALL] end click', {
      isCallComplete: this.callStore.getIsCallComplete()
    });

    this.sendAmplitudeClickEndCallEvent();

    if (!this.controlService) return;

    if (this.controlService.isVisualAidCall && !this.controlService.realtimeService) {
      await this.handleVisualAidCallEnd();
    } else {
      this.controlService.userInactivity = false;
      this.controlService.endCall();
    }
  }

  protected get isLoading() {
    return this.playerService?.showLoadingScreen;
  }

  public avatarVideoIsReady(item: any) {
    console.log('AVATAR::Ready', item);
    this.enableVisualAid = true;
  }

  avatarLoadingError(event: Event, item: any): void {
    const videoElement = event.target as HTMLVideoElement;
    const error = videoElement.error;

    if (error) {
      switch (error.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          console.error('Video load aborted by user or browser:', item.src);
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          console.error('Network error while loading video:', item.src);
          break;
        case MediaError.MEDIA_ERR_DECODE:
          console.error('Decoding error while playing video:', item.src);
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          console.error('Video format or source not supported:', item.src);
          break;
        default:
          console.error('Unknown video error occurred:', item.src);
      }
    } else {
      console.error('An unknown error occurred while loading video:', item.src);
    }
  }

}
